import { Divider, Grid, Popover, Tab, Tabs } from '@material-ui/core';
import { Icons, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useMst } from '../../mobx-models/Root';
import { Notification } from '../../rxdb/notification/queryBuilder';
import { useNotification } from '../../rxdb/notification/useNotification';
import useNotificationData from '../../rxdb/notification/useNotificationData';
import { WORK_PACKAGE_NAV_TAB } from '../../utilities/constants';
import { NotificationTab } from '../../utilities/enums';
import { trackEventFn, TrackingEventType } from '../../utilities/hooks/useEventTracking';
import {
  getFormattedDateForNotification,
  getRole,
  setValueinLocalStorage,
} from '../../utilities/utilityFunctions';
import CustomAvatarIcon from '../AppBar/CustomAvatarIcon';
import { useStyles } from './NotificationDialog.styles';

interface Props {
  open: boolean;
  anchorEl: HTMLDivElement | null;
  onClose: () => void;
}

const NotificationDialog: React.FC<Props> = ({ open, anchorEl, onClose }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { HandleGesture, SiteDiscipline, CurrentUserSelected, WorkPackageTabs } = useMst();
  const { setNotificationAsRead, setNotificationAsDisabled } = useNotification();
  const { unreadNotificationList, readNotificationList } = useNotificationData(
    SiteDiscipline.siteId,
    SiteDiscipline.disciplineId,
  );

  const [isExpand, setIsExpand] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(NotificationTab.UN_READ);

  const isUnReadTabOpen = tabValue === NotificationTab.UN_READ;
  const notificationData = isUnReadTabOpen ? unreadNotificationList : readNotificationList;

  const handleTabChange = (e: React.ChangeEvent, value: number) => {
    setTabValue(value);
    setIsExpand(false);
    e.stopPropagation();
  };

  const handleBulkNotificationClick = async () => {
    const filterNotificationList = notificationData.filter(notification =>
      isUnReadTabOpen ? !notification?.isRead : notification.isRead,
    );
    const notificationIdList = filterNotificationList.map(
      notification => notification.pkUSerAlertId,
    );
    if (isUnReadTabOpen) {
      await setNotificationAsRead(notificationIdList);
    } else {
      await setNotificationAsDisabled(notificationIdList);
    }
    await trackEventFn(TrackingEventType.NOTIFICATION_BULK_WRITE, {
      action: isUnReadTabOpen ? 'read' : 'delete',
      notificationIdList,
    });
  };

  const handleNotificationClick = async (notification: Notification) => {
    const alertMetaData = JSON.parse(notification?.alertMetaData || '{}');
    const { scheduleId, inspectionId, taskId } = alertMetaData;
    const isAdhoc = alertMetaData.isAdhoc?.toLowerCase() === 'true';
    if (scheduleId) {
      setValueinLocalStorage('workPackageId', scheduleId);
      CurrentUserSelected.setWorkPackageId(Number(scheduleId));
    }
    if (inspectionId) {
      setValueinLocalStorage('inspectionID', inspectionId);
      CurrentUserSelected.setInspectionId(Number(inspectionId));
    }
    if (taskId) {
      setValueinLocalStorage('taskId', taskId);
      CurrentUserSelected.setTaskId(Number(taskId));
    }
    setValueinLocalStorage('isAdhoc', isAdhoc);
    CurrentUserSelected.setIsAdhoc(isAdhoc);
    WorkPackageTabs.setTab(
      isAdhoc ? WORK_PACKAGE_NAV_TAB.ADHOC.VALUE : WORK_PACKAGE_NAV_TAB.WORKPACKAGE.VALUE,
    );
    HandleGesture.setIsNotificationClicked(true);

    if (taskId) {
      history.push('/inspection-details');
    } else if (!isAdhoc && (inspectionId || scheduleId)) {
      history.push('/inspection-list');
    } else {
      history.push('/work-packages');
    }

    if (!notification.isRead) await setNotificationAsRead([notification.pkUSerAlertId]);
    onClose();
    setTabValue(NotificationTab.UN_READ);
  };

  return (
    <Popover
      id="notification-dialog"
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        setTabValue(NotificationTab.UN_READ);
        onClose();
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={{ paper: !isExpand ? classes.paper : classes.paper1 }}
    >
      <Grid>
        <Grid className={classes.header}>
          <Typography align="center">Notification</Typography>
        </Grid>
        <Grid container xs={12} className={classes.notificationFirstGrid}>
          <Grid item xs={8}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              aria-label="secondary tabs example"
              className={classes.notificationTabs}
              TabIndicatorProps={{ style: { width: '50%' } }}
            >
              <Tab
                label={`Unread (${unreadNotificationList?.length})`}
                className={classes.notificationSubTab}
              />
              <Tab
                label={`Read (${readNotificationList?.length})`}
                className={classes.notificationSubTab}
                data-testid="readId"
              />
            </Tabs>
          </Grid>
          <Grid item xs={4} className={classes.markAsRead}>
            <Typography
              color="primary"
              variant="caption"
              onClick={handleBulkNotificationClick}
              data-testid="markAllRead"
            >
              {isUnReadTabOpen ? 'Mark all as Read' : 'Clear All'}
            </Typography>
          </Grid>
        </Grid>
        <Grid className={isExpand ? classes.body : classes.body1}>
          {notificationData?.length > 0 ? (
            notificationData
              ?.sort((a: Notification, b: Notification) =>
                Number(a.pkUSerAlertId) < Number(b.pkUSerAlertId) ? 1 : -1,
              )
              .filter((val, index) => (!isExpand ? index < 3 : true))
              .map((item: Notification, index: number) => {
                const isOldNotification = item.isRead && item.alertMetaData === null;
                return (
                  <Grid
                    item
                    container
                    xs={12}
                    key={item.pkUSerAlertId}
                    justifyContent="flex-start"
                    onClick={() => {
                      if (!isOldNotification) {
                        handleNotificationClick(item);
                      }
                    }}
                    className={
                      isOldNotification ? classes.notficationGridDisabled : classes.notificationGrid
                    }
                  >
                    <Grid item xs={2} className={classes.iconPos}>
                      <CustomAvatarIcon />
                    </Grid>
                    <Grid item xs={7}>
                      <Typography
                        className={classes.siteCoordinatorName}
                        variant="inherit"
                        fontWeight={400}
                      >
                        {`${item.firstName} ${item.lastName}`}
                      </Typography>
                      <Typography className={classes.role} fontWeight={400} variant="caption">
                        {getRole(item.roleId)}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={400}
                        className={
                          isOldNotification ? classes.disabledAlertText : classes.alertText
                        }
                      >
                        {item.alertText}
                      </Typography>
                    </Grid>
                    <Grid item container xs={3} justifyContent="flex-end">
                      <Typography className={classes.timing} fontWeight={400} variant="caption">
                        {getFormattedDateForNotification(item.datecreated)}
                        <br />
                        <Icons.Trash2Feather
                          color="error"
                          className={classes.trashIcon}
                          onClick={event => {
                            setNotificationAsDisabled([item.pkUSerAlertId]);
                            event.stopPropagation();
                          }}
                          data-testid="trashIcon"
                        />
                      </Typography>
                    </Grid>
                    {notificationData?.filter((val, valIndex) => (!isExpand ? valIndex < 3 : true))
                      ?.length !==
                      index + 1 && <Divider orientation="horizontal" className={classes.divider} />}
                  </Grid>
                );
              })
          ) : (
            <Grid container className={classes.noNotificationGrid} justifyContent="center">
              <Typography className={classes.noNotificationLabel}>No Notification found</Typography>
            </Grid>
          )}
        </Grid>
        {notificationData?.length > 3 && (
          <Grid className={classes.showMoreGrid}>
            <Grid onClick={() => setIsExpand(prev => !prev)}>
              <Typography color="primary" fontWeight={600}>
                {`Show ${isExpand ? 'Less' : 'More'}`}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Popover>
  );
};

export default observer(NotificationDialog);
